import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { cn } from '../../lib/cn';

function DraggableItem({ id, children, disabled = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cn(
        'px-3 py-2 rounded-lg text-sm font-medium cursor-grab active:cursor-grabbing',
        'bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600',
        'hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm',
        'transition-all duration-150 select-none touch-none',
        isDragging && 'opacity-50 shadow-lg',
        disabled && 'opacity-60 cursor-not-allowed'
      )}
    >
      {children}
    </div>
  );
}

function DroppableZone({ id, children, isOver, label }) {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[120px] p-3 rounded-lg border-2 border-dashed transition-all duration-200',
        isOver
          ? 'border-gray-900 dark:border-white bg-gray-100 dark:bg-gray-700'
          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
      )}
    >
      {label && (
        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
          {label}
        </div>
      )}
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function ItemChip({ item, onRemove }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600">
      <span>{item}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label={t('tableCompletion.removeItem', { item })}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function TableCompletion({
  options = {},
  value = {},
  onChange,
  disabled = false,
  className,
}) {
  const { t } = useTranslation();
  const { bank = [], columns = [] } = options;
  const placements = value?.placements || {};
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Items still in the word bank (not placed in any column)
  const bankItems = bank.filter((item) => placements[item] === undefined);

  // Items in each column
  const getColumnItems = (columnIndex) => {
    return bank.filter((item) => placements[item] === columnIndex);
  };

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = useCallback((event) => {
    setOverId(event.over?.id || null);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      setOverId(null);

      if (!over || disabled) return;

      const item = active.id;
      const targetId = over.id;

      const newPlacements = { ...placements };

      if (targetId === 'bank') {
        // Remove from placements (back to bank)
        delete newPlacements[item];
      } else if (targetId.startsWith('column-')) {
        // Place in column
        const columnIndex = parseInt(targetId.replace('column-', ''), 10);
        newPlacements[item] = columnIndex;
      }

      console.log('[TableCompletion] handleDragEnd - Calling onChange with:', { placements: newPlacements });
      onChange({ placements: newPlacements });
    },
    [placements, onChange, disabled]
  );

  const handleRemoveFromColumn = useCallback(
    (item) => {
      if (disabled) return;
      const newPlacements = { ...placements };
      delete newPlacements[item];
      onChange({ placements: newPlacements });
    },
    [placements, onChange, disabled]
  );

  const activeItem = activeId ? bank.find((item) => item === activeId) : null;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Word Bank */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('tableCompletion.wordBank')}
          </label>
          <DroppableZone id="bank" isOver={overId === 'bank'}>
            {bankItems.length > 0 ? (
              bankItems.map((item) => (
                <DraggableItem key={item} id={item} disabled={disabled}>
                  {item}
                </DraggableItem>
              ))
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
                {t('tableCompletion.allItemsPlaced')}
              </p>
            )}
          </DroppableZone>
        </div>

        {/* Category Columns */}
        <div
          className={cn(
            'grid gap-4',
            columns.length === 2 && 'grid-cols-2',
            columns.length === 3 && 'grid-cols-3',
            columns.length >= 4 && 'grid-cols-2 md:grid-cols-4'
          )}
        >
          {columns.map((column, index) => {
            const columnItems = getColumnItems(index);
            const dropId = `column-${index}`;

            return (
              <div key={index} className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                  {column.label}
                </label>
                <DroppableZone id={dropId} isOver={overId === dropId}>
                  {columnItems.length > 0 ? (
                    columnItems.map((item) => (
                      <DraggableItem key={item} id={item} disabled={disabled}>
                        {item}
                      </DraggableItem>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center">
                      {t('tableCompletion.dropItemsHere')}
                    </p>
                  )}
                </DroppableZone>
              </div>
            );
          })}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeItem ? (
            <div className="px-3 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-700 border-2 border-gray-900 dark:border-white shadow-lg">
              {activeItem}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Progress indicator */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t('tableCompletion.itemsPlaced', { placed: Object.keys(placements).length, total: bank.length })}
      </div>
    </div>
  );
}
